import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiError, formatGBP, formatGBPCompact, formatGBP2, businessLoanCost, DEFAULT_INDICATIVE_APR } from "@/lib/api";
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, Building2, TrendingUp, Landmark, Shield, Zap, Save, FileText, Download, Trophy } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StatusBadge } from "./AdminDashboard";

const STATUSES = ["new", "contacted", "approved", "rejected"];

export default function ApplicationDetail() {
  const { id } = useParams();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [bids, setBids] = useState([]);

  const loadAux = async () => {
    try {
      const [dRes, bRes] = await Promise.all([
        api.get(`/applications/${id}/documents`),
        api.get(`/admin/applications/${id}/bids`),
      ]);
      setDocs(dRes.data); setBids(bRes.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/applications/${id}`);
        setApp(data); setNotes(data.notes || "");
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    loadAux();
    // eslint-disable-next-line
  }, [id]);

  if (!ready) return <div className="min-h-screen bg-navy-deep text-white/50 flex items-center justify-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      setApp(data); toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/applications/${id}`, { notes });
      setApp(data); toast.success("Notes saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const selectBid = async (bidId) => {
    if (!window.confirm("Award this bid? All other bids will be marked lost and applicant + lenders will be notified.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/applications/${id}/select-bid`, { bid_id: bidId });
      setApp(data);
      loadAux();
      toast.success("Bid awarded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!app) return <div className="min-h-screen bg-navy-deep text-white/50 flex items-center justify-center">Loading application…</div>;

  const { monthly, total } = businessLoanCost(app.amount, app.term_months);
  const contactName = `${app.first_name || ""} ${app.last_name || ""}`.trim() || "—";

  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-navy-deep/90 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-5">
            <button onClick={() => nav("/admin")} data-testid="app-back-btn" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/70 hover:text-white">
              <ArrowLeft className="h-4 w-4" strokeWidth={2.4} /> Pipeline
            </button>
            <div className="h-5 w-px bg-white/15" />
            <Link to="/" className="flex items-center gap-2">
              <LogoMark size={32} />
              <div className="font-display font-extrabold text-[16px] tracking-[-0.035em]">Swift<span className="text-emerald">Cash</span></div>
            </Link>
          </div>
          <StatusBadge status={app.status} />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-10 md:py-14 space-y-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-4">
            Application · {(app.id || "").slice(0, 8)}
          </div>
          <h1 className="font-display font-extrabold text-[48px] md:text-[72px] leading-[0.95] tracking-[-0.04em]">
            {app.company_name || contactName || "—"}
          </h1>
          <p className="mt-3 font-mono text-[12px] uppercase tracking-[0.12em] text-white/45 font-semibold">
            Submitted {new Date(app.submitted_at).toLocaleString("en-GB")} · {contactName} ({app.role || "—"})
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-3">Requested</div>
            <div className="font-display font-extrabold text-[52px] leading-none tracking-[-0.04em] mb-3">{formatGBPCompact(app.amount)}</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/55 mb-7 font-bold">
              {app.term_months} {app.term_months === 1 ? "month" : "months"} · {app.purpose || "—"}
            </div>
            <div className="space-y-4 pt-5 border-t border-white/10">
              <KV label="Indicative monthly" value={formatGBP2(monthly)} />
              <KV label="Indicative APR" value={`${(DEFAULT_INDICATIVE_APR * 100).toFixed(1)}%`} />
              <KV label="Indicative total" value={formatGBP(total)} />
            </div>
          </div>

          <div className="lg:col-span-2 rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-5">Business & contact</div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
              <IconRow icon={<Building2 className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Company" value={app.company_name} />
              <IconRow icon={<FileText className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Companies House" value={app.company_number || "—"} />
              <IconRow icon={<Briefcase className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Sector" value={app.sector} />
              <IconRow icon={<MapPin className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Business postcode" value={app.business_postcode} />
              <IconRow icon={<Briefcase className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Trading years" value={app.trading_years} />
              <IconRow icon={<Landmark className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Business bank" value={app.business_bank || "—"} />
              <IconRow icon={<TrendingUp className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Annual turnover" value={formatGBP(app.annual_turnover || 0)} />
              <IconRow icon={<TrendingUp className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Monthly revenue" value={formatGBP(app.monthly_revenue || 0)} />
              <IconRow icon={<TrendingUp className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Existing debt" value={formatGBP(app.existing_debt || 0)} />
              <IconRow icon={<Shield className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Contact" value={`${contactName}${app.role ? " · " + app.role : ""}`} />
              <IconRow icon={<Mail className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Email" value={app.email} />
              <IconRow icon={<Phone className="h-3.5 w-3.5" strokeWidth={2.4} />} label="Phone" value={app.phone} />
            </div>
          </div>
        </div>

        <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-5">Update status</div>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                disabled={saving || app.status === s}
                data-testid={`app-status-${s}`}
                className={`px-5 h-11 rounded-full text-[13px] font-extrabold capitalize transition-colors border
                  ${app.status === s ? "bg-emerald text-white border-emerald shadow-emerald" : "bg-transparent border-white/15 text-white/80 hover:border-white/40 hover:bg-white/5"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45">Internal notes</div>
            <button onClick={saveNotes} disabled={saving} data-testid="app-save-notes-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald text-white hover:bg-emerald-2 text-[13px] font-extrabold disabled:opacity-50">
              <Save className="h-3.5 w-3.5" strokeWidth={2.6} /> Save
            </button>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder="Add notes for your team…"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-emerald focus-visible:border-emerald font-medium"
            data-testid="app-notes-textarea"
          />
        </div>

        {/* Documents */}
        <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-4">Documents · {docs.length}</div>
          {docs.length === 0 && (
            <div className="text-white/50 font-semibold text-[14px]">No documents attached.</div>
          )}
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-row-${d.id}`}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <FileText className="h-4 w-4 text-white/50 shrink-0" strokeWidth={2.2} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] text-white font-semibold truncate">{d.original_filename}</div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-white/45 font-bold">
                    {d.category.replace(/_/g, " ")} · {(d.size / 1024).toFixed(0)}kb
                  </div>
                </div>
                <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    const t = localStorage.getItem("sc_token");
                    fetch(`${API}/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${t}` } })
                      .then((r) => r.blob()).then((b) => {
                        const u = URL.createObjectURL(b);
                        const a = document.createElement("a");
                        a.href = u; a.download = d.original_filename; a.click();
                        URL.revokeObjectURL(u);
                      });
                  }}
                  data-testid={`doc-download-${d.id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 text-white/80 hover:bg-white/10 text-[12px] font-bold">
                  <Download className="h-3.5 w-3.5" strokeWidth={2.4} /> Download
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Bids */}
        <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-7">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-4 inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald pulse-dot" /> Lender bids · {bids.length}
          </div>
          {bids.length === 0 && (
            <div className="text-white/50 font-semibold text-[14px]">No bids yet. Deal is visible to approved lenders on the panel.</div>
          )}
          <div className="space-y-2">
            {bids.map((b, i) => {
              const isWinner = app?.winning_bid_id === b.id;
              return (
                <div key={b.id} data-testid={`bid-row-${b.id}`}
                  className={`grid grid-cols-12 gap-3 items-center px-4 py-3 rounded-lg border
                    ${isWinner ? "bg-emerald/[0.08] border-emerald/40" : b.status === "lost" ? "bg-white/[0.02] border-white/10 opacity-60" : "bg-white/[0.03] border-white/10"}`}>
                  <div className="col-span-1 font-display font-extrabold text-[18px] text-white/60">#{i + 1}</div>
                  <div className="col-span-3">
                    <div className="font-display font-extrabold text-[20px] tracking-[-0.02em]">{b.apr.toFixed(2)}%</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-white/45 font-bold">APR</div>
                  </div>
                  <div className="col-span-3 font-mono text-[12px] text-white/70 font-bold">
                    {b.arrangement_fee_pct.toFixed(2)}% arr. · {formatGBP2(b.monthly_fee)}/mo
                  </div>
                  <div className="col-span-3 text-[12.5px] text-white/70 font-semibold truncate">{b.lender_company}</div>
                  <div className="col-span-2 text-right">
                    {isWinner ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald text-white text-[10px] font-mono font-extrabold uppercase tracking-[0.1em]">
                        <Trophy className="h-3 w-3" strokeWidth={3} /> Won
                      </span>
                    ) : b.status === "lost" ? (
                      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-white/40 font-bold">Lost</span>
                    ) : (
                      <button onClick={() => selectBid(b.id)} data-testid={`select-bid-${b.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald text-white text-[11px] font-extrabold hover:bg-emerald-2">
                        <Trophy className="h-3 w-3" strokeWidth={3} /> Award
                      </button>
                    )}
                  </div>
                  {b.terms_note && (
                    <div className="col-span-12 text-[12px] text-white/60 font-medium mt-1 leading-relaxed">
                      Terms: {b.terms_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/50">{label}</span>
      <span className="font-display font-extrabold text-[16px] text-white text-right tracking-[-0.02em]">{value}</span>
    </div>
  );
}
function IconRow({ icon, label, value }) {
  return (
    <div>
      <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-1.5 inline-flex items-center gap-1.5">
        <span className="text-white/35">{icon}</span>{label}
      </div>
      <div className="text-[14px] text-white break-words font-semibold">{value || "—"}</div>
    </div>
  );
}
